import React from "react";
import { useNavigate } from "react-router-dom";
import assets from "../../assets";
import { SessionSummaryCard } from "../../common-components/score-card";
import useHandler, {
  getFeedbackSummary,
  TOPICS,
  TOPICS_BY_DIFFICULTY,
  DIFFICULTIES,
  MIC_STATE,
  getScoreTier,
} from "./handler";
import "./style.sass";
import { ROUTE_DASHBOARD } from "../../route/communicative";
import { useState, useRef, useEffect } from "react";

const Header = ({ content }) => (
  <header className="conversation_header">
    <div className="header_left">
      <img src={assets.ic_logo} alt="Rovian AI" className="logo" />
      <span className="logo_title">Rovian AI</span>
    </div>
    {content}
  </header>
);

const Conversation = ({ onEndConversation } = {}) => {
  const {
    screen,
    topic,
    setTopic,
    difficulty,
    setDifficulty,
    canStart,
    starting,
    startConversation,
    messages,
    micState,
    selectedFeedback,
    setSelectedFeedback,
    chatBodyRef,
    handleMicTap,
    endConversation,
    ending,
    summaryResult,
    backToDashboard,
  } = useHandler({ onEndConversation });

  const navigate = useNavigate();
  const [playingIndex, setPlayingIndex] = useState(null);
  const audioRef = useRef(null);
  const lastAutoPlayedCountRef = useRef(0); // how many messages we've already auto-played for

  const playAudio = (index, url) => {
    if (!url) return;

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(url);
    audio.onended = () => setPlayingIndex(null);
    audio.play().catch(() => {
      // Some browsers block autoplay until the user interacts with the
      // page. If that happens, just leave it unplayed — the play button
      // on the bubble still works for a manual tap.
      setPlayingIndex(null);
    });
    audioRef.current = audio;
    setPlayingIndex(index);
  };

  const toggleAudio = (index, url) => {
    if (playingIndex === index) {
      audioRef.current?.pause();
      setPlayingIndex(null);
      return;
    }
    playAudio(index, url);
  };

  // Auto-play every new AI message's audio as soon as it arrives —
  // greeting from /start and every reply from /chat. Guarded by
  // lastAutoPlayedCountRef so this only fires for genuinely new messages,
  // not on unrelated re-renders (e.g. typing in the topic input).
  useEffect(() => {
    if (messages.length <= lastAutoPlayedCountRef.current) return;

    const newMessages = messages.slice(lastAutoPlayedCountRef.current);
    lastAutoPlayedCountRef.current = messages.length;

    const newAiMessageWithAudio = newMessages.find((m) => m.role === "ai" && m.audioUrl);
    if (newAiMessageWithAudio) {
      const index = messages.indexOf(newAiMessageWithAudio);
      playAudio(index, newAiMessageWithAudio.audioUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const goToDashboard = () => navigate(ROUTE_DASHBOARD);

  // ===================== Render helpers (per-item renderers; .map() is called at the JSX site) =====================

  const renderDashboardLink = () => (
    <div className="back_link" onClick={goToDashboard}>
      {/* <img
        src={assets.ic_back_arrow || ""}
        alt=""
        style={{ transform: "rotate(0deg)", width: "12px" }}
      />{" "} */}
      <span className="back-arrow">←</span>
      Dashboard
    </div>
  );

  const renderTopicTags = (t) => (
    <button
      key={t}
      type="button"
      className={`tag ${topic === t ? "active" : ""}`}
      onClick={() => setTopic(t)}
    >
      <span className="tag_dot" />
      <span className="tag_text">{t}</span>
      {topic === t && <span className="tag_check">✓</span>}
    </button>
  );

  const renderDifficultyOptions = (d) => (
    <button
      key={d}
      type="button"
      className={`difficulty_btn ${difficulty === d ? "active" : ""}`}
      onClick={() => setDifficulty(d)}
    >
      {d}
    </button>
  );

  const renderChatMessage = (m, i) => (
    <div key={i} className={`chat_bubble_row ${m.role === "user" ? "user_row" : "ai_row"}`}>
      <div className={`chat_bubble ${m.role === "user" ? "user_bubble" : "ai_bubble"}`}>
        {m.role === "ai" && m.audioUrl && (
          <div className="bubble_audio_wrapper">
            {playingIndex === i && (
              <div className="sound_wave_animation">
                <span className="wave_bar" />
                <span className="wave_bar" />
                <span className="wave_bar" />
                <span className="wave_bar" />
              </div>
            )}
            <button
              type="button"
              className={`audio_play_btn ${playingIndex === i ? "playing" : ""}`}
              onClick={() => toggleAudio(i, m.audioUrl)}
            >
              {playingIndex === i ? (
                <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10">
                  <rect x="4" y="4" width="4" height="16" rx="1"></rect>
                  <rect x="14" y="4" width="4" height="16" rx="1"></rect>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              )}
            </button>
          </div>
        )}
        {m.text}
      </div>
      {m.role === "user" && m.feedback && (
        <div className="feedback_tag">
          <button className="view_feedback" type="button" onClick={() => setSelectedFeedback(m.feedback)}>
            View feedback
          </button>
          <span className={`feedback_score feedback_score_${getScoreTier(m.feedback.score)}`}>
            {m.feedback.score}%
          </span>
        </div>
      )}
    </div>
  );

  const renderMicFooter = () => {
    if (micState === MIC_STATE.TRANSCRIBING) {
      return (
        <>
          <div className="mic_btn transcribing_btn">
            <span className="transcribe_spinner" />
          </div>
          <span className="mic_label">Transcribing</span>
        </>
      );
    }

    return (
      <>
        <button
          className={`mic_btn ${micState === MIC_STATE.RECORDING ? "recording_btn" : "idle_btn"}`}
          type="button"
          onClick={handleMicTap}
        >
          {micState === MIC_STATE.RECORDING ? (
            <span className="stop_icon" />
          ) : (
            <span className="mic_icon">
              <img src={assets.ic_mic_white} alt="mic" width={22} height={22} />
            </span>
          )}
        </button>
        <span className="mic_label">{micState === MIC_STATE.RECORDING ? "Tap to send" : "Tap and speak"}</span>
      </>
    );
  };

  const renderFeedbackPanel = () => {
    if (!selectedFeedback) return null;

    const summary = getFeedbackSummary(selectedFeedback.score);

    return (
      <div className="feedback_panel">
        <div className="feedback_panel_header">
          <div className="feedback_panel_title">
            <img src={assets.ic_grammar_green} alt="grammar" className="feedback_panel_icon" />
            Grammar Feedback
          </div>
          <button
            className="feedback_panel_close"
            type="button"
            onClick={() => setSelectedFeedback(null)}
            aria-label="Close feedback panel"
          >
            &times;
          </button>
        </div>

        <div className="feedback_panel_body">
          <div className="feedback_summary_card">
            <div className="feedback_summary_icon">
              <img src={assets.ic_excellent} alt="excellent" />
            </div>
            <div className="feedback_summary_text">
              <strong>{selectedFeedback.score_heading}</strong>
              <p>{summary.message}</p>
            </div>
          </div>

          <div className="overall_score_row">
            <span>Overall Score</span>
            <span className="overall_score_value">{selectedFeedback.score} / 100</span>
          </div>
          <div className="score_bar_track">
            <div className="score_bar_fill" style={{ width: `${selectedFeedback.score}%` }} />
          </div>

          <div className="feedback_section">
            <div className="feedback_section_title">
              <span className="dot red_dot" />
              Grammar Mistakes
            </div>
            <p className="feedback_section_text">{selectedFeedback.mistakes}</p>
          </div>

          <div className="feedback_section">
            <div className="feedback_section_title">
              <span className="dot green_dot" />
              Corrected Version
            </div>
            <p className="feedback_section_text">{selectedFeedback.corrected}</p>
          </div>
        </div>
      </div>
    );
  };

  // ===================== SETUP =====================
  if (screen === "setup") {
    return (
      <div className="conversation_practice">
        <Header content={renderDashboardLink()} />

        <main className="conversation_main">
          <div className="conversation_card">
            <h1>Conversation Practice</h1>
            <p className="subtitle">
              Select your preferences to start an AI conversation practice
              session.
            </p>

            <div className="form_group">
              <label>Difficulty</label>
              <div className="difficulty_options">{DIFFICULTIES.map(renderDifficultyOptions)}</div>
            </div>

            <div className="form_group">
              <label>Topic</label>
              <input
                type="text"
                placeholder="Enter topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
              />
              <div className="topic_tags_header">
                <span className="topic_tags_label">Suggested Topics</span>
              </div>
              <div className="topic_tags">
                {((difficulty && TOPICS_BY_DIFFICULTY[difficulty]) || TOPICS_BY_DIFFICULTY.Beginner || TOPICS).map(renderTopicTags)}
              </div>
            </div>

            <button
              className="start_btn"
              type="button"
              onClick={startConversation}
              disabled={!canStart || starting}
            >
              {starting ? "Starting…" : "Start Conversation"}
            </button>
          </div>
        </main>
      </div>
    );
  }

  // ===================== SUMMARY =====================
  if (screen === "summary") {
    const { overallScore, metrics, feedbackMessage, close_heading } = summaryResult;

    return (
      <div className="conversation_practice">
        <Header content={renderDashboardLink()} />

        <main className="summary_main">
          <h1>Session Summary</h1>
          <p className="summary_subtitle">Here's how you performed in your conversation practice.</p>

          <SessionSummaryCard
            score={overallScore}
            title={close_heading}
            message={feedbackMessage}
            metrics={metrics}
          />
        </main>
      </div>
    );
  }

  // ===================== CHAT =====================
  return (
    <div className="conversation_practice">
      <div className="chat_layout">
        <div className="chat_card">
          <header className="chat_header">
            <div className="chat_header_left">
              <span className="chat_difficulty">{difficulty}</span>
              <span className="chat_topic">{topic}</span>
            </div>
            <button
              className="end_score_btn"
              type="button"
              onClick={endConversation}
              disabled={ending}
            >
              {ending ? "Scoring…" : "End & Get Score"}
            </button>
          </header>

          <div className="chat_body" ref={chatBodyRef}>
            <div className="chat_thread">{messages.map(renderChatMessage)}</div>
          </div>

          <div className="chat_footer">{renderMicFooter()}</div>
        </div>

        {renderFeedbackPanel()}
      </div>
    </div>
  );
};

export default Conversation;
